import { REVISION_FILE_LIMIT_COPY, VIDEO_FILE_LIMIT_COPY } from '../../config/uploadConfig';
import { EDITOR_HOME_PATH } from '../../utils/editorAccount';

/** The formats the video uploader accepts (UploadVideo reads and plays them). */
export const VIDEO_FORMATS_COPY = 'MP4, MOV or WebM';

export const SUPPORT_EMAIL = 'info@artbits.solutions';

/** "Up to 1 GB…" -> "up to 1 GB…", for use mid-sentence. */
const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

export interface GettingStartedStep {
  title: string;
  body: string;
  /** Where the step is done. Every path is a route in App.tsx. */
  link?: { to: string; label: string };
}

/**
 * A creator's path from raw footage to a delivered edit, each step linked to
 * the page where it happens.
 */
export const CREATOR_STEPS: GettingStartedStep[] = [
  {
    title: 'Upload a video',
    body: `Add the footage you want edited to your library. ${VIDEO_FORMATS_COPY}. ${VIDEO_FILE_LIMIT_COPY}.`,
    link: { to: '/upload', label: 'Upload a video' },
  },
  {
    title: 'Annotate it',
    body: 'Open the video from your library and leave notes at exact frames: cuts, music, colour, text on screen.',
    link: { to: '/library', label: 'Open library' },
  },
  {
    title: 'Create a project',
    body: 'Choose the annotated video, then add a brief, a deadline and any reference images or audio.',
    link: { to: '/submit-editing-request', label: 'New project' },
  },
  {
    title: 'Invite or assign an editor',
    body: 'Invite editors by email. Assign one when you create the project, or later from Projects.',
    link: { to: '/my-editors', label: 'Invite an editor' },
  },
  {
    title: 'Review cuts',
    body: 'Each cut your editor uploads opens on the project page. Approve it, or request changes with a comment.',
    link: { to: '/my-projects?status=needs-review', label: 'Projects to review' },
  },
  {
    title: 'Close the project',
    body: 'Once you approve a cut, download it from the project page and close the project to mark it delivered.',
    link: { to: '/my-projects?status=done', label: 'Delivered projects' },
  },
];

/**
 * An editor-only account can't open the creator pages above (the router
 * sends it to its projects), so it gets its own steps and one way in.
 */
export const EDITOR_STEPS: GettingStartedStep[] = [
  {
    title: 'Accept the project',
    body: 'Projects a creator assigns to you appear in Projects. Open one to accept or decline it.',
  },
  {
    title: 'Read the brief and the notes',
    body: "The brief, the creator's notes at exact frames and any reference files are all in the project.",
  },
  {
    title: 'Download the footage',
    body: 'Get the source video and the reference files from the project.',
  },
  {
    title: 'Work through the tasks',
    body: 'Mark tasks as you finish them so the creator can follow your progress.',
  },
  {
    title: 'Upload your cut',
    body: 'Deliver each version from the project. The creator approves it or requests changes.',
  },
  {
    title: 'Keep the conversation in the project',
    body: 'Ask questions and answer the creator in the project, so nothing gets lost in email.',
  },
];

export const EDITOR_HOME_LINK = { to: EDITOR_HOME_PATH, label: 'Open your projects' };

export interface FaqEntry {
  question: string;
  answer: string;
}

export const FAQS: FaqEntry[] = [
  {
    question: 'What video formats can I upload?',
    answer: `${VIDEO_FORMATS_COPY}. Files are kept exactly as you upload them, so your editor works from your original footage. If a file won't open, convert it to MP4 (H.264) and upload it again.`,
  },
  {
    question: 'How detailed should my notes be?',
    answer:
      'The more specific, the better. Note the timing of cuts, transitions, colour, music cues and any effects you want. Think of your notes as the creative brief for your editor.',
  },
  {
    question: 'Can I work with my own editors?',
    answer:
      'Yes. Editors join PithPlay by invitation: you invite the editors you work with by email, and they see only the projects you assign them, with your notes and brief.',
  },
  {
    question: 'How do revisions work?',
    answer:
      'Your editor uploads each cut to the project as a new version (v1, v2 and so on). Watch it on the project page, then approve it or request changes. Your plan sets how many change rounds each project gets.',
  },
  {
    question: 'What happens to my original files?',
    answer:
      'Your originals stay in your library. Editors download them to work on the projects you assign and deliver their edits as new versions, so an edit never overwrites your original.',
  },
  {
    question: 'Is there a limit on file size or project length?',
    answer: `Each video file can be ${lowerFirst(VIDEO_FILE_LIMIT_COPY)}. Your plan also sets your total storage (1 GB, 10 GB or 25 GB), how many projects can be active at once and how many change rounds each project gets. There is no separate limit on video length.`,
  },
];

/** For editor-only accounts: the questions an editor actually has. */
export const EDITOR_FAQS: FaqEntry[] = [
  {
    question: 'Do I pay for PithPlay?',
    answer: "No. Editors don't pay; the creator's plan covers the projects they send you.",
  },
  {
    question: 'Where do I find the footage?',
    answer: 'In the project. Download the original video and any reference images or audio the creator attached.',
  },
  {
    question: 'How do revisions work?',
    answer:
      "Upload each cut to the project as a new version (v1, v2 and so on). The creator approves it or requests changes, and the creator's plan sets how many change rounds each project gets.",
  },
  {
    question: 'How big can a cut be?',
    answer: `Each cut can be ${REVISION_FILE_LIMIT_COPY}.`,
  },
];
